import styled, { css } from "styled-components/macro";
import { Select, MenuItem, Tooltip } from "@mui/material";
import { useDispatch } from "react-redux";
import { updateTrendChartBinSize } from "../../providers/GlobalStateProvider";
import { ELEMENT_BORDER_RADIUS_CSS } from "../../constants/constants";
import {
  getAllowedBinSizes,
  useFocusRange,
  useTrendChartBinSize,
  useTrendsPageFilterState,
} from "utils/customHooks/genericHooks";
import {
  darkBlue,
  darkGrey,
  darkModeBackgroundColor,
  darkModeDarkTextColor,
  darkModeLinkColor,
  darkModeModalBackgroundColor,
  darkModeTextColor,
} from "constants/colors";
import { FONT_SIZE_SMALL } from "constants/constants";
import { startCase } from "lodash";
import { useIsDarkMode } from "store/atoms";
import { TrendChartBinSizesEnum } from "types/enums";
import { TRENDS_BIN_SIZE_VALUES_MS_MAP } from "utils/timeUtils";

const TrendChartBinSizeDropdownStyles = styled.div<{ $isDarkMode: boolean }>`
  width: fit-content;
  margin: auto;
  position: relative;
  background: ${({ $isDarkMode }) =>
    $isDarkMode ? darkModeBackgroundColor : "#f5f5f5"};
  display: flex;
  align-items: center;
  padding: 0.5em 1.2em;
  ${ELEMENT_BORDER_RADIUS_CSS}
  .MuiSelect-select {
    font-weight: bold;
    padding-bottom: 2px;
  }
  .selectLabel {
    padding-right: 0.5em;
    color: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeDarkTextColor : darkGrey};
  }
  .MuiInputBase-root {
    margin-bottom: 0.25em;
  }
  .MuiSelect-select.MuiSelect-select {
    padding-right: 1.5em;
    background: none;
    transform: translateY(2px);
  }
  .MuiInput-underline:before {
    border-bottom-color: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeLinkColor : darkBlue} !important;
  }
  .MuiSelect-icon {
    color: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeLinkColor : darkBlue} !important;
    margin-top: 0.1em;
    margin-right: -0.2em;
  }
  .MuiSelect-root {
    color: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeLinkColor : darkBlue} !important;
    ${({ $isDarkMode }) =>
      $isDarkMode
        ? css`
            background: none !important;
          `
        : ""}
    padding-right: 0.75em;
    padding-bottom: 0;
    padding-left: 0.1em;
    font-size: ${FONT_SIZE_SMALL}px;
    font-weight: bold;
    &:first-letter {
      text-transform: uppercase;
    }
  }
  @media print {
    margin-top: 2em;
  }
`;

const TrendChartBinSizeDropdown = () => {
  const dispatch = useDispatch();
  const { trendChartBinSize } = useTrendChartBinSize();
  const { isTrendsPageFilteredByShift } = useTrendsPageFilterState();

  const focusRange = useFocusRange();
  const duration = focusRange ? focusRange.lt - focusRange.ge : 0;
  const isTrendsPage24HourMode = duration <= TRENDS_BIN_SIZE_VALUES_MS_MAP.days;
  const isShiftly =
    isTrendsPageFilteredByShift &&
    trendChartBinSize === TrendChartBinSizesEnum.hours &&
    isTrendsPage24HourMode;

  const allowedBinSizes: TrendChartBinSizesEnum[] =
    getAllowedBinSizes(duration);

  const handleBinSizeDropdownChange = event => {
    const newTrendChartBinSize = event.target.value;
    if (trendChartBinSize !== newTrendChartBinSize) {
      dispatch(updateTrendChartBinSize(newTrendChartBinSize));
    }
  };
  const [isDarkMode] = useIsDarkMode();
  const disabled = allowedBinSizes.length === 1;
  return (
    <TrendChartBinSizeDropdownStyles
      className="doNotPrint"
      data-component-name={"Bin Size Dropdown"}
      $isDarkMode={isDarkMode}
    >
      <span className="selectLabel">Display in:</span>
      <Tooltip
        title={disabled ? "There's only one bin size for this range" : ""}
      >
        <Select
          className="dropdown"
          displayEmpty={true}
          inputProps={{
            id: "trends-page-bin-size",
          }}
          variant="standard"
          onChange={handleBinSizeDropdownChange}
          value={
            allowedBinSizes.includes(trendChartBinSize) ? trendChartBinSize : ""
          }
          disabled={disabled}
          MenuProps={{
            disableScrollLock: true,
            ...(isDarkMode
              ? {
                  PaperProps: {
                    style: {
                      backgroundColor: darkModeModalBackgroundColor,
                      color: darkModeTextColor,
                    },
                  },
                }
              : {}),
          }}
        >
          {allowedBinSizes.map(binSize => (
            <MenuItem
              key={binSize}
              value={binSize}
              data-testid={`trendspage-trendchart-binsize-dropdown-menu-item-${binSize}`}
            >
              {isShiftly ? "Shifts" : startCase(binSize)}
            </MenuItem>
          ))}
        </Select>
      </Tooltip>
    </TrendChartBinSizeDropdownStyles>
  );
};

export default TrendChartBinSizeDropdown;
